import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from 'react-bootstrap';
import { GroupStandingsTieBreakerEnum } from '../../../../../types/enums';
import {
  DEFAULT_TEAM_MATCHUP_SCORING,
  GROUP_STANDINGS_TIE_BREAKER_LABEL_KEYS,
  GroupStandingsTieBreakerResolution,
  hasRequiredGroupStandingsTieBreakers,
  listIncludesTeamPoints,
  listUsesV2TeamStandingsCriteria,
  TeamMatchupScoring,
} from '../../../../../utils/groupStandingsTieBreakers';
import { GroupStandingsTieBreakerEditor } from './GroupStandingsTieBreakerEditor';
import './GroupStandingsTieBreakerSection.scss';

export type TieBreakerChainContextScope = 'organization' | 'tour' | 'tournament' | 'class';

type ChainRowDef = {
  scope: keyof GroupStandingsTieBreakerResolution['chain'];
  labelKey: string;
  tieBreakers: GroupStandingsTieBreakerEnum[] | null;
};

const SCOPES_BY_CONTEXT: Record<
  TieBreakerChainContextScope,
  Array<keyof GroupStandingsTieBreakerResolution['chain']>
> = {
  organization: ['organization', 'default'],
  tour: ['tour', 'organization', 'default'],
  tournament: ['tournament', 'tour', 'organization', 'default'],
  class: ['class', 'tournament', 'tour', 'organization', 'default'],
};

const ALL_ROW_DEFS: ChainRowDef[] = [
  { scope: 'class', labelKey: 'groupStandingsTieBreaker-sourceClass', tieBreakers: null },
  {
    scope: 'tournament',
    labelKey: 'groupStandingsTieBreaker-sourceTournament',
    tieBreakers: null,
  },
  { scope: 'tour', labelKey: 'groupStandingsTieBreaker-sourceTour', tieBreakers: null },
  {
    scope: 'organization',
    labelKey: 'groupStandingsTieBreaker-sourceOrganization',
    tieBreakers: null,
  },
  { scope: 'default', labelKey: 'groupStandingsTieBreaker-sourceDefault', tieBreakers: null },
];

function buildScopeRowsForContext(
  resolution: GroupStandingsTieBreakerResolution,
  context: TieBreakerChainContextScope
): ChainRowDef[] {
  const allowed = new Set(SCOPES_BY_CONTEXT[context]);
  return ALL_ROW_DEFS.filter((def) => allowed.has(def.scope)).map((def) => ({
    ...def,
    tieBreakers: resolution.chain[def.scope],
  }));
}

type GroupStandingsTieBreakerSectionProps = {
  value: GroupStandingsTieBreakerEnum[];
  onChange: (nextValue: GroupStandingsTieBreakerEnum[]) => void;
  description?: ReactNode;
  inheritChecked?: boolean;
  onInheritChange?: (checked: boolean) => void;
  inheritLabelKey?: string;
  editorDisabled?: boolean;
  inheritDisabled?: boolean;
  className?: string;
  showTitle?: boolean;
  resolution?: GroupStandingsTieBreakerResolution;
  /** Which inheritance levels apply in this editor (hides invalid child scopes). */
  chainContextScope?: TieBreakerChainContextScope;
  isTeamsClass?: boolean;
  scoringValue?: TeamMatchupScoring;
  onScoringChange?: (nextValue: TeamMatchupScoring) => void;
  inheritScoringChecked?: boolean;
  onInheritScoringChange?: (checked: boolean) => void;
  inheritScoringLabelKey?: string;
  showOptInRankingWarning?: boolean;
};

export const GroupStandingsTieBreakerSection = ({
  value,
  onChange,
  description,
  inheritChecked,
  onInheritChange,
  inheritLabelKey,
  editorDisabled = false,
  inheritDisabled = false,
  className,
  showTitle = true,
  resolution,
  chainContextScope = 'class',
  isTeamsClass,
  scoringValue,
  onScoringChange,
  inheritScoringChecked,
  onInheritScoringChange,
  inheritScoringLabelKey,
  showOptInRankingWarning = false,
}: GroupStandingsTieBreakerSectionProps) => {
  const { t } = useTranslation();
  const [detailRow, setDetailRow] = useState<ChainRowDef | null>(null);

  const hasInheritanceToggle =
    typeof inheritChecked === 'boolean' && Boolean(onInheritChange) && Boolean(inheritLabelKey);
  const showHeader = showTitle || hasInheritanceToggle;

  const scopeRows = useMemo(() => {
    if (!resolution) return [];
    return buildScopeRowsForContext(resolution, chainContextScope);
  }, [resolution, chainContextScope]);

  const inheritedMode = Boolean(hasInheritanceToggle && inheritChecked);

  const hasScoringEditor = Boolean(onScoringChange) && listIncludesTeamPoints(value);
  const scoringInheritMode =
    typeof inheritScoringChecked === 'boolean' &&
    Boolean(onInheritScoringChange) &&
    Boolean(inheritScoringLabelKey);
  const displayedScoring: TeamMatchupScoring =
    scoringValue ?? resolution?.teamMatchupScoring ?? DEFAULT_TEAM_MATCHUP_SCORING;
  const scoringInputsDisabled =
    editorDisabled || (scoringInheritMode && Boolean(inheritScoringChecked));

  const setScoringField = (field: keyof TeamMatchupScoring, raw: string) => {
    const parsed = Number(raw);
    const nextValue = Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
    onScoringChange?.({ ...displayedScoring, [field]: nextValue });
  };

  const showParentScopeV2Warning =
    chainContextScope !== 'class' && listUsesV2TeamStandingsCriteria(value);
  const showRankingChangeWarning =
    !inheritedMode && (showOptInRankingWarning || showParentScopeV2Warning);

  const inheritedListMissingCore =
    Boolean(isTeamsClass) &&
    inheritedMode &&
    Boolean(resolution) &&
    resolution!.source !== 'class' &&
    !hasRequiredGroupStandingsTieBreakers(resolution!.effective, { isTeamsClass: true });

  const activeSourceLabelKey = resolution
    ? `groupStandingsTieBreaker-source${resolution.source[0].toUpperCase()}${resolution.source.slice(
        1
      )}`
    : '';

  return (
    <section
      className={`group-standings-tie-breaker-section ${className || ''}`.trim()}
      data-inherited={inheritedMode}
    >
      {showHeader && (
        <div className="group-standings-tie-breaker-section__header">
          {showTitle ? (
            <span className="group-standings-tie-breaker-section__title">
              {t('groupStandingsTieBreaker-title')}
            </span>
          ) : (
            <span />
          )}
          {hasInheritanceToggle && (
            <label className="group-standings-tie-breaker-section__inherit">
              <input
                type="checkbox"
                checked={inheritChecked}
                onChange={(event) => onInheritChange?.(event.target.checked)}
                disabled={inheritDisabled}
              />
              <span>{t(inheritLabelKey as string)}</span>
            </label>
          )}
        </div>
      )}
      {description ? (
        <p className="group-standings-tie-breaker-section__description">{description}</p>
      ) : null}
      {chainContextScope !== 'class' ? (
        <p className="group-standings-tie-breaker-section__description">
          {t(
            listUsesV2TeamStandingsCriteria(value)
              ? 'groupStandingsTieBreaker-parentScopeRequiredHintV2'
              : 'groupStandingsTieBreaker-parentScopeRequiredHint'
          )}
        </p>
      ) : null}
      <p className="group-standings-tie-breaker-section__hint">
        {t('groupStandingsTieBreaker-reorderHint')}
      </p>
      {inheritedMode ? (
        <p className="group-standings-tie-breaker-section__readonly-note">
          {t('groupStandingsTieBreaker-inheritedReadonly')}
        </p>
      ) : null}
      {inheritedListMissingCore ? (
        <p
          className="group-standings-tie-breaker-section__warning"
          data-testid="group-standings-tie-breaker-inherited-missing-core"
        >
          {t('groupStandingsTieBreaker-inheritedMissingCoreWarning')}
        </p>
      ) : null}
      {resolution ? (
        <div className="group-standings-tie-breaker-section__chain">
          <div className="group-standings-tie-breaker-section__chain-header">
            <span>{t('groupStandingsTieBreaker-inheritanceChainTitle')}</span>
            <span className="group-standings-tie-breaker-section__source-badge">
              {t('groupStandingsTieBreaker-activeSource')}:&nbsp;
              {t(activeSourceLabelKey)}
            </span>
          </div>
          <p className="group-standings-tie-breaker-section__chain-hint">
            {t('groupStandingsTieBreaker-inheritanceChainHint')}
          </p>
          <div className="group-standings-tie-breaker-section__chain-body">
            {scopeRows.map((row) => {
              const isActive = row.scope === resolution.source;
              const configured = Boolean(row.tieBreakers?.length);
              return (
                <div
                  key={row.scope}
                  className="group-standings-tie-breaker-section__chain-row"
                  data-active={isActive}
                >
                  <span className="group-standings-tie-breaker-section__chain-label">
                    {t(row.labelKey)}
                  </span>
                  <span className="group-standings-tie-breaker-section__chain-status">
                    {configured
                      ? t('groupStandingsTieBreaker-configuredStatus')
                      : t('groupStandingsTieBreaker-notConfigured')}
                  </span>
                  <span className="group-standings-tie-breaker-section__chain-actions">
                    {configured ? (
                      <button
                        type="button"
                        className="group-standings-tie-breaker-section__chain-details-btn"
                        onClick={() => setDetailRow(row)}
                      >
                        {t('groupStandingsTieBreaker-viewDetails')}
                      </button>
                    ) : null}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      <GroupStandingsTieBreakerEditor
        value={value}
        onChange={onChange}
        disabled={editorDisabled}
        isTeamsClass={isTeamsClass}
      />
      {showRankingChangeWarning ? (
        <p
          className="group-standings-tie-breaker-section__warning"
          data-testid="group-standings-tie-breaker-opt-in-warning"
        >
          {t('groupStandingsTieBreaker-optInRankingChangeWarning')}
        </p>
      ) : null}
      {hasScoringEditor ? (
        <div className="group-standings-tie-breaker-section__scoring">
          <div className="group-standings-tie-breaker-section__scoring-header">
            <span className="group-standings-tie-breaker-section__scoring-title">
              {t('teamMatchupScoring-title')}
            </span>
            {scoringInheritMode ? (
              <label className="group-standings-tie-breaker-section__inherit">
                <input
                  type="checkbox"
                  checked={Boolean(inheritScoringChecked)}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    onInheritScoringChange?.(checked);
                    if (!checked) {
                      onScoringChange?.(
                        resolution?.teamMatchupScoring ?? DEFAULT_TEAM_MATCHUP_SCORING
                      );
                    }
                  }}
                  disabled={inheritDisabled}
                />
                <span>{t(inheritScoringLabelKey as string)}</span>
              </label>
            ) : null}
          </div>
          <p className="group-standings-tie-breaker-section__description">
            {t('teamMatchupScoring-description')}
          </p>
          <div className="group-standings-tie-breaker-section__scoring-fields">
            {(['win', 'draw', 'loss'] as const).map((field) => (
              <label key={field} className="group-standings-tie-breaker-section__scoring-field">
                <span>{t(`teamMatchupScoring-${field}`)}</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  disabled={scoringInputsDisabled}
                  value={displayedScoring[field]}
                  onChange={(event) => setScoringField(field, event.target.value)}
                />
              </label>
            ))}
          </div>
        </div>
      ) : null}

      <Modal show={detailRow !== null} onHide={() => setDetailRow(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title as="h6" className="mb-0">
            {detailRow ? t(detailRow.labelKey) : ''}
            {detailRow ? ` - ${t('groupStandingsTieBreaker-chainDetailSubtitle')}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailRow?.tieBreakers?.length ? (
            <ol className="group-standings-tie-breaker-section__detail-list">
              {detailRow.tieBreakers.map((tieBreaker, index) => (
                <li key={`${tieBreaker}-${index}`}>
                  {t(GROUP_STANDINGS_TIE_BREAKER_LABEL_KEYS[tieBreaker])}
                </li>
              ))}
            </ol>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-dark" onClick={() => setDetailRow(null)}>
            {t('modal-cancel')}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};
